import { Smartphone, Store, UserCog, MessageSquare } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

/**
 * Display-only badge showing how an order was created, rendered alongside
 * the status pill and QbSyncedMark. Designed to slot into the same
 * flex-nowrap container as QbSyncedMark (Phase 3c invariant) so all three
 * facts — status, QB-sync, source — stay inline across order views.
 *
 * Sources:
 *   - customer_app  — placed by the customer in the public ordering page
 *   - kiosk         — placed via the kiosk variant of the customer ordering page
 *   - admin_manual  — entered by an admin/staff on the customer's behalf
 *                     (also includes pre-2026-03-23 legacy rows where we
 *                     can't tell — conservative default per the backfill)
 *   - sms_agent     — placed by the text-to-order SMS agent ("robot Kyle")
 *
 * For backward compatibility with pre-source rows that may not carry the
 * field, missing source is treated as admin_manual.
 *
 * Relies on the app-root <TooltipProvider> in App.tsx.
 */
export type OrderSource = "customer_app" | "kiosk" | "admin_manual" | "sms_agent";

interface OrderSourceBadgeProps {
  source: OrderSource | string | null | undefined;
  /** Display square size in px. Default 14 — slightly smaller than QbSyncedMark (16) so it reads as secondary. */
  size?: number;
  className?: string;
}

const META: Record<OrderSource, { Icon: typeof Smartphone; label: string; colorClass: string }> = {
  customer_app: {
    Icon: Smartphone,
    label: "Placed via customer app",
    colorClass: "text-emerald-600",
  },
  kiosk: {
    Icon: Store,
    label: "Placed via kiosk",
    colorClass: "text-sky-600",
  },
  admin_manual: {
    Icon: UserCog,
    label: "Entered by admin / staff",
    colorClass: "text-gray-500",
  },
  sms_agent: {
    Icon: MessageSquare,
    label: "Placed by text (SMS agent)",
    colorClass: "text-violet-600",
  },
};

function normalize(source: OrderSourceBadgeProps["source"]): OrderSource {
  if (source === "customer_app" || source === "kiosk" || source === "admin_manual" || source === "sms_agent") return source;
  return "admin_manual";
}

export function OrderSourceBadge({ source, size = 14, className }: OrderSourceBadgeProps) {
  const resolved = normalize(source);
  const { Icon, label, colorClass } = META[resolved];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center cursor-default select-none ${colorClass} ${className ?? ""}`}
          data-testid="order-source-badge"
          data-source={resolved}
        >
          <Icon size={size} strokeWidth={2} />
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
